import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTeamContext } from '@/contexts/TeamContext'
import { ChevronDown, Users, Settings, Check, Crown, Plus } from 'lucide-react'
import { cn } from '@/utils/cn'
import { toast } from 'react-hot-toast'
import CreateTeamModal from './CreateTeamModal'
import '@/design-system/shell.css'

interface TeamSwitcherProps {
  className?: string
}

export default function TeamSwitcher({ className }: TeamSwitcherProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const {
    currentTeam,
    allTeams,
    isLoading,
    currentTeamError,
    switchTeam,
    isSwitchingTeam,
    hasMultipleTeams,
    createTeam,
    isCreatingTeam,
    ownsTeam
  } = useTeamContext()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSwitchTeam = async (teamId: string) => {
    try {
      await switchTeam(teamId)
      toast.success('Switched team successfully')
      setIsOpen(false)
      // React Query will automatically refetch invalidated queries
    } catch (error) {
      console.error('Failed to switch team:', error)
      toast.error('Failed to switch team')
    }
  }

  const handleCreateTeam = async (organizationName?: string) => {
    try {
      await createTeam(organizationName)
      toast.success('Team created successfully!')
      setShowCreateModal(false)
      setIsOpen(false)
      // React Query will automatically refetch invalidated queries
    } catch (error: any) {
      console.error('Failed to create team:', error)
      if (error?.response?.data?.error?.includes('maximum limit')) {
        toast.error('You have reached the maximum limit of 10 teams')
      } else {
        toast.error('Failed to create team. Please try again.')
      }
    }
  }

  // If currently loading, show nothing (will render quickly)
  if (isLoading) {
    return null
  }

  // If there's an error or no team data, don't render the switcher
  // This handles cases where teams feature is disabled or there's a persistent error
  // Temporary API errors (like rate limits) will resolve when queries retry
  if (!currentTeam && currentTeamError) {
    return null
  }

  // If we have no current team but no error, we're still loading
  if (!currentTeam) {
    return null
  }

  // Get team member info for current team
  const currentTeamMember = allTeams?.teams.find(
    (t) => t.teamId === currentTeam.team.id
  )

  // Determine display name with smart fallback:
  // 1. Try organization name from current team
  // 2. Try organization name from team member
  // 3. For owned teams: "My Team"
  // 4. For member teams: owner's name + "'s Team" or owner's email
  const getDisplayName = () => {
    if (currentTeam.team.organizationName) return currentTeam.team.organizationName
    if (currentTeamMember?.organizationName) return currentTeamMember.organizationName
    if (currentTeamMember?.isOwner) return 'My Team'
    // For teams where user is a member, show owner's info
    if (currentTeamMember?.ownerFirstName && currentTeamMember?.ownerLastName) {
      return `${currentTeamMember.ownerFirstName} ${currentTeamMember.ownerLastName}'s Team`
    }
    if (currentTeamMember?.ownerEmail) return `${currentTeamMember.ownerEmail}'s team`
    return 'Team'
  }
  const displayName = getDisplayName()

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ds-trigger"
        disabled={isSwitchingTeam}
      >
        <Users aria-hidden="true" />
        <span>{displayName}</span>
        {hasMultipleTeams && (
          <ChevronDown className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} />
        )}
      </button>

      {isOpen && (
        <div className="ds-drop">
          {/* Current Team Header */}
          <div className="ds-drop__head">
            <div>
              <div className="ds-drop__label">Current Team</div>
              <div className="ds-drop__name">{displayName}</div>
            </div>
            <div className="ds-drop__count">
              <Users aria-hidden="true" />
              <span>{currentTeam.members.length}</span>
            </div>
          </div>

          {/* Team List - Only show if multiple teams */}
          {hasMultipleTeams && allTeams && allTeams.teams.length > 1 && (
            <div className="ds-drop__sect">
              <div className="ds-drop__secthead ds-drop__label">
                Switch Team
              </div>
              <div className="space-y-0.5">
                {allTeams.teams.map((team) => {
                  const isCurrentTeam = team.teamId === currentTeam.team.id
                  // Smart display name: org name > owner name's Team > owner email's team > fallback
                  const getTeamDisplayName = () => {
                    if (team.organizationName) return team.organizationName
                    if (team.isOwner) return 'My Team'
                    // For teams where user is a member, show owner's info
                    if (team.ownerFirstName && team.ownerLastName) {
                      return `${team.ownerFirstName} ${team.ownerLastName}'s Team`
                    }
                    if (team.ownerEmail) return `${team.ownerEmail}'s team`
                    return 'Team'
                  }
                  const teamDisplayName = getTeamDisplayName()

                  return (
                    <button
                      key={team.id}
                      onClick={() => !isCurrentTeam && handleSwitchTeam(team.teamId)}
                      disabled={isCurrentTeam || isSwitchingTeam}
                      className={cn(
                        'ds-drop__row',
                        isCurrentTeam && 'ds-picked',
                        isSwitchingTeam && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <div className="ds-drop__what">
                        {team.isOwner ? (
                          <Crown aria-hidden="true" />
                        ) : (
                          <Users aria-hidden="true" />
                        )}
                        <span>{teamDisplayName}</span>
                      </div>
                      {isCurrentTeam && <Check aria-hidden="true" />}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="ds-drop__sect">
            {!ownsTeam && (
              <button
                onClick={() => {
                  setShowCreateModal(true)
                  setIsOpen(false)
                }}
                className="ds-drop__row"
              >
                <div className="ds-drop__what">
                  <Plus aria-hidden="true" />
                  <span>Create a new Team</span>
                </div>
              </button>
            )}
            <Link
              to="/team/settings"
              onClick={() => setIsOpen(false)}
              className="ds-drop__row"
            >
              <div className="ds-drop__what">
                <Settings aria-hidden="true" />
                <span>Team Settings</span>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Create Team Modal */}
      <CreateTeamModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateTeam}
        isCreating={isCreatingTeam}
      />
    </div>
  )
}
